import { useFetchInventoryOrders } from '@/hooksApi/useInventory';
import { InventoryOrder } from '@/types/inventory';
import { OrderDataType } from './useOrderData';
import { MerchantTitleOrderStatusEnum, OrderStatusEnum } from '@/config';
import { formatDateByDayjs } from '@/utils';

/**
 * Fetches inventory orders and maps them to the shared `OrderDataType` format
 * so they can be rendered by the same `TableAction` + `getColumns()` used by
 * every other order tab.
 *
 * Also exposes `rawItems` so callers can reconstruct the original `InventoryOrder`
 * objects needed by the Resend / Return-Now flows.
 */
const useInventoryAsOrderData = (
  currentPage: number,
  pageSize: number,
  enabled: boolean = true,
) => {
  const { data, isLoading, isFetching, refetch } = useFetchInventoryOrders(
    currentPage,
    pageSize,
    enabled,
  );

  const rawItems: InventoryOrder[] = data?.object?.items ?? [];
  const total: number = isFetching || isLoading ? 0 : (data?.object?.total ?? 0);

  const inventoryStatus = {
    slug: OrderStatusEnum.inventory,
    merchantTitle: MerchantTitleOrderStatusEnum.inventory,
  };

  const activeData: OrderDataType[] =
    isFetching || isLoading
      ? []
      : rawItems.map((item) => ({
          key: item.uid,
          orderDate: formatDateByDayjs(item.created_at, 'D MMMM YYYY'),
          orderDateRaw: item.created_at,
          orderId: item.platform_code,
          customerName: item.full_name,
          customerAddress: item.address_text,
          orderValue: item.fee ?? 0,
          platform_code: item.platform_code,
          custom_id: '---',
          status: inventoryStatus,
          pickupLocation: '---',
          pickupAddressId: null,
          products: (item.products ?? []).map((p) => ({
            name: p.title,
            quantity: p.quantity,
            price: p.price,
          })),
          driver: { phone: undefined, assign: false },
          merchant_action_required: 0,
          merchant_action_required_text: '',
          carrier_message: '',
          phone: item.phone,
        }));

  return {
    activeData,
    rawItems,
    totals: total,
    isLoading,
    isFetching,
    refetch,
  };
};

export default useInventoryAsOrderData;
